import { ThemeOptions, createTheme } from '@mui/material/styles'

import { COLORS } from './constants/Colors'
import { TEXT } from './constants/Text'

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    display1: true
    display2: true
    display3: true
    display4: true
    display5: true
    caption2: true
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    poster: React.CSSProperties
  }
  interface TypographyVariantsOptions {
    poster?: React.CSSProperties
  }
  interface Palette {
    gradient?: {
      main: string
      contrastText: string
    }
    tertiary?: {
      main: string
      contrastText: string
    }
  }
  interface PaletteOptions {
    gradient?: {
      main: string
      contrastText: string
    }
    tertiary?: {
      main: string
      contrastText: string
    }
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    gradient: true
    tertiary: true
  }
}

const generateType = (token: any) => ({
  ...token,
  letterSpacing: `${token.letterSpacing}px`,
})

const typography = {
  display1: generateType(TEXT.DISPLAY_1),
  display2: generateType(TEXT.DISPLAY_2),
  display3: generateType(TEXT.DISPLAY_3),
  display4: generateType(TEXT.DISPLAY_4),
  display5: generateType(TEXT.DISPLAY_5),
  h1: generateType(TEXT.H1),
  h2: generateType(TEXT.H2),
  h3: generateType(TEXT.H3),
  h4: generateType(TEXT.H4),
  h5: generateType(TEXT.H5),
  h6: generateType(TEXT.H6),
  subtitle1: generateType(TEXT.SUBTITLE1),
  body1: generateType(TEXT.BODY1),
  body2: generateType(TEXT.BODY2),
  button: generateType(TEXT.BUTTON),
  caption: generateType(TEXT.CAPTION),
  caption2: generateType(TEXT.CAPTION2),
}

const customTheme = {
  breakpoints: {
    values: {
      xs: 0,
      sm: 640,
      md: 768,
      lg: 1024,
      xl: 1280,
    },
  },
  typography,
  palette: {
    common: {
      black: COLORS.BASE_BLACK,
      white: COLORS.BASE_WHITE,
    },
    type: 'dark',
    primary: {
      '25': COLORS.PRIMARY_25,
      '50': COLORS.PRIMARY_50,
      '100': COLORS.PRIMARY_100,
      '200': COLORS.PRIMARY_200,
      '300': COLORS.PRIMARY_300,
      '400': COLORS.PRIMARY_400,
      '500': COLORS.PRIMARY_500,
      '600': COLORS.PRIMARY_600,
      '700': COLORS.PRIMARY_700,
      '800': COLORS.PRIMARY_800,
      '900': COLORS.PRIMARY_900,
      light: COLORS.PRIMARY_200,
      main: COLORS.PRIMARY_500,
      dark: COLORS.PRIMARY_700,
      contrastText: COLORS.BASE_WHITE,
    },
    secondary: {
      '25': COLORS.SECONDARY_25,
      '50': COLORS.SECONDARY_50,
      '100': COLORS.SECONDARY_100,
      '200': COLORS.SECONDARY_200,
      '300': COLORS.SECONDARY_300,
      '400': COLORS.SECONDARY_400,
      '500': COLORS.SECONDARY_500,
      '600': COLORS.SECONDARY_600,
      '700': COLORS.SECONDARY_700,
      '800': COLORS.SECONDARY_800,
      '900': COLORS.SECONDARY_900,
      light: COLORS.SECONDARY_200,
      main: COLORS.SECONDARY_500,
      dark: COLORS.SECONDARY_700,
      contrastText: COLORS.BASE_WHITE,
    },
    error: {
      '25': COLORS.ERROR_25,
      '50': COLORS.ERROR_50,
      '100': COLORS.ERROR_100,
      '200': COLORS.ERROR_200,
      '300': COLORS.ERROR_300,
      '400': COLORS.ERROR_400,
      '500': COLORS.ERROR_500,
      '600': COLORS.ERROR_600,
      '700': COLORS.ERROR_700,
      '800': COLORS.ERROR_800,
      '900': COLORS.ERROR_900,
      light: COLORS.ERROR_200,
      main: COLORS.ERROR_500,
      dark: COLORS.ERROR_700,
      contrastText: COLORS.BASE_WHITE,
    },
    warning: {
      '25': COLORS.WARNING_25,
      '50': COLORS.WARNING_50,
      '100': COLORS.WARNING_100,
      '200': COLORS.WARNING_200,
      '300': COLORS.WARNING_300,
      '400': COLORS.WARNING_400,
      '500': COLORS.WARNING_500,
      '600': COLORS.WARNING_600,
      '700': COLORS.WARNING_700,
      '800': COLORS.WARNING_800,
      '900': COLORS.WARNING_900,
      light: COLORS.WARNING_200,
      main: COLORS.WARNING_500,
      dark: COLORS.WARNING_700,
      contrastText: COLORS.BASE_WHITE,
    },
    info: {
      '25': COLORS.INFO_25,
      '50': COLORS.INFO_50,
      '100': COLORS.INFO_100,
      '200': COLORS.INFO_200,
      '300': COLORS.INFO_300,
      '400': COLORS.INFO_400,
      '500': COLORS.INFO_500,
      '600': COLORS.INFO_600,
      '700': COLORS.INFO_700,
      '800': COLORS.INFO_800,
      '900': COLORS.INFO_900,
      light: COLORS.INFO_200,
      main: COLORS.INFO_500,
      dark: COLORS.INFO_700,
      contrastText: COLORS.BASE_WHITE,
    },
    success: {
      '25': COLORS.SUCCESS_25,
      '50': COLORS.SUCCESS_50,
      '100': COLORS.SUCCESS_100,
      '200': COLORS.SUCCESS_200,
      '300': COLORS.SUCCESS_300,
      '400': COLORS.SUCCESS_400,
      '500': COLORS.SUCCESS_500,
      '600': COLORS.SUCCESS_600,
      '700': COLORS.SUCCESS_700,
      '800': COLORS.SUCCESS_800,
      '900': COLORS.SUCCESS_900,
      light: COLORS.SUCCESS_200,
      main: COLORS.SUCCESS_500,
      dark: COLORS.SUCCESS_700,
      contrastText: COLORS.BASE_WHITE,
    },
    graygreen: {
      '25': COLORS.GRAY_GREEN_25,
      '50': COLORS.GRAY_GREEN_50,
      '100': COLORS.GRAY_GREEN_100,
      '200': COLORS.GRAY_GREEN_200,
      '300': COLORS.GRAY_GREEN_300,
      '400': COLORS.GRAY_GREEN_400,
      '500': COLORS.GRAY_GREEN_500,
      '600': COLORS.GRAY_GREEN_600,
      '700': COLORS.GRAY_GREEN_700,
      '800': COLORS.GRAY_GREEN_800,
      '900': COLORS.GRAY_GREEN_900,
      light: COLORS.GRAY_GREEN_200,
      main: COLORS.GRAY_GREEN_500,
      dark: COLORS.GRAY_GREEN_700,
      contrastText: COLORS.BASE_WHITE,
    },
    grey: {
      '25': COLORS.GRAY_25,
      '50': COLORS.GRAY_50,
      '100': COLORS.GRAY_100,
      '200': COLORS.GRAY_200,
      '300': COLORS.GRAY_300,
      '400': COLORS.GRAY_400,
      '450': COLORS.GRAY_450,
      '500': COLORS.GRAY_500,
      '600': COLORS.GRAY_600,
      '700': COLORS.GRAY_700,
      '800': COLORS.GRAY_800,
      '900': COLORS.GRAY_900,
      A100: COLORS.GRAY_100,
      A200: COLORS.GRAY_200,
      A400: COLORS.GRAY_400,
      A700: COLORS.GRAY_700,
    },
    yellow: {
      '25': COLORS.YELLOW_25,
      '50': COLORS.YELLOW_50,
      '100': COLORS.YELLOW_100,
      '200': COLORS.YELLOW_200,
      '300': COLORS.YELLOW_300,
      '400': COLORS.YELLOW_400,
      '500': COLORS.YELLOW_500,
      '600': COLORS.YELLOW_600,
      '700': COLORS.YELLOW_700,
      '800': COLORS.YELLOW_800,
      '900': COLORS.YELLOW_900,
      A100: COLORS.YELLOW_100,
      A200: COLORS.YELLOW_200,
      A400: COLORS.YELLOW_400,
      A700: COLORS.YELLOW_700,
    },
    brick: {
      '25': COLORS.BRICK_25,
      '50': COLORS.BRICK_50,
      '100': COLORS.BRICK_100,
      '200': COLORS.BRICK_200,
      '300': COLORS.BRICK_300,
      '400': COLORS.BRICK_400,
      '500': COLORS.BRICK_500,
      '600': COLORS.BRICK_600,
      '700': COLORS.BRICK_700,
      '800': COLORS.BRICK_800,
      '900': COLORS.BRICK_900,
      A100: COLORS.BRICK_100,
      A200: COLORS.BRICK_200,
      A400: COLORS.BRICK_400,
      A700: COLORS.BRICK_700,
    },
    text: {
      primary: COLORS.GRAY_700,
      secondary: COLORS.GRAY_600,
      disabled: COLORS.GRAY_400,
      hint: COLORS.GRAY_500,
    },
  },

  components: {
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: 'white',
          display: 'none',
        },
      },
    },

    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: '36px',
          padding: '8px 12px !important',
          fontWeight: 600,
          textTransform: 'none',
          borderRadius: '6px',
          '&.Mui-selected': {
            color: '#36575a',
            backgroundColor: '#e1e7e8 !important',
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: COLORS.GRAY_50,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          padding: '10px 16px',
          fontSize: '0.875rem',
          fontWeight: 500,
          lineHeight: 1.43,
          color: 'white',
          textTransform: 'none',
          borderRadius: '100px',
          fontFamily: 'Mulish',
        },
        contained: {
          color: COLORS.BASE_WHITE,
          backgroundColor: COLORS.PRIMARY_600,
          '&.button-large': {
            fontSize: '16px',
            lineHeight: 1.5,
          },
          '&:disabled': {
            color: COLORS.BASE_WHITE,
            backgroundColor: COLORS.PRIMARY_200,
          },
          '&.button-delete, &.button-error': {
            backgroundColor: COLORS.ERROR_600,
          },
        },
        containedPrimary: {},
        containedSecondary: {},
        outlined: {
          color: COLORS.GRAY_700,
          borderColor: COLORS.GRAY_200,
        },
        outlinedPrimary: {},
        outlinedSecondary: {},
        text: {
          color: COLORS.PRIMARY_700,
        },
      },
    },
  },
}

const theme = createTheme(customTheme as ThemeOptions)

export { theme, customTheme }
